/**
 * MissionToolbar Component
 *
 * Action buttons for mission operations
 */

import { useState, useEffect } from 'react';
import { Plus, Undo2, Redo2, FolderOpen, Trash2 } from 'lucide-react';
import { useMission } from '../hooks/useMission';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  removeAllWaypointMarkers,
} from '@/features/mission/utils/cesium-waypoint-renderer.ts';
import { loadMissionFromFile } from '../utils/file-operations';
import { MissionSettingsPanel } from './MissionSettingsPanel';
import { ValidationPanel } from './ValidationPanel';
import { validateMission, getValidationSummary } from '../utils/mission-validator';
import { useMissionStore } from '@/stores/useMissionStore';
import type { Viewer as CesiumViewer } from 'cesium';

interface MissionToolbarProps {
  viewer?: CesiumViewer;
  className?: string;
}

export function MissionToolbar({ viewer, className }: MissionToolbarProps) {
  const {
    mission,
    isAddingWaypoint,
    setAddingWaypointMode,
    undo,
    redo,
    canUndo,
    canRedo,
    clearAllWaypoints,
  } = useMission();

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isValidationOpen, setIsValidationOpen] = useState(false);
  const [_validationStatus, setValidationStatus] = useState<{
    isValid: boolean;
    errorCount: number;
    warningCount: number;
  }>({ isValid: true, errorCount: 0, warningCount: 0 });

  // Update validation status when mission changes
  useEffect(() => {
    if (mission) {
      const validation = validateMission(mission);
      const summary = getValidationSummary(validation);
      setValidationStatus({
        isValid: validation.isValid,
        errorCount: summary.errors,
        warningCount: summary.warnings,
      });
    } else {
      setValidationStatus({ isValid: true, errorCount: 0, warningCount: 0 });
    }
  }, [mission]);

  const handleAddWaypoint = () => {
    setAddingWaypointMode(!isAddingWaypoint);
    if (!isAddingWaypoint) {
      toast.info('Click on the map to add a waypoint', {
        duration: 3000,
      });
    }
  };



  const handleClearAll = () => {
    if (confirm(`Delete all ${mission?.waypoints.length} waypoints?`)) {
      clearAllWaypoints();
      if (viewer) {
        removeAllWaypointMarkers(viewer);
      }
      toast.success('All waypoints cleared');
    }
  };



  const handleLoad = async () => {
    try {
      const loadedMission = await loadMissionFromFile();

      // Set loaded mission as current mission in store
      useMissionStore.setState({ currentMission: loadedMission });

      toast.success(`Mission "${loadedMission.name}" loaded`);
    } catch (error) {
      if (error instanceof Error && error.message === 'No file selected') {
        // User cancelled file picker, don't show error
        return;
      }
      toast.error('Failed to load mission');
      console.error('Load mission error:', error);
    }
  };



  const handleUndo = () => {
    undo();
    toast.info('Undo');
  };

  const handleRedo = () => {
    redo();
    toast.info('Redo');
  };

  return (
    <div className={cn('p-3 backdrop-blur-sm ', className)}>
      <div className="flex items-center justify-between gap-2">
        {/* Left side - Action buttons */}
        <div className="flex items-center gap-2">
          {/* Add Waypoint */}
          <ToolbarButton
            icon={<Plus className="w-4 h-4" />}
            label="Add Waypoint"
            onClick={handleAddWaypoint}
            active={isAddingWaypoint}
            variant={isAddingWaypoint ? 'primary' : 'default'}
          />

          <div className="w-px h-6 bg-slate-700" />

          {/* Undo */}
          <ToolbarButton
            icon={<Undo2 className="w-4 h-4" />}
            label="Undo"
            onClick={handleUndo}
            disabled={!canUndo}
            shortcut="Ctrl+Z"
          />

          {/* Redo */}
          <ToolbarButton
            icon={<Redo2 className="w-4 h-4" />}
            label="Redo"
            onClick={handleRedo}
            disabled={!canRedo}
            shortcut="Ctrl+Shift+Z"
          />

          <div className="w-px h-6 bg-slate-700" />

          {/* Load */}
          <ToolbarButton
            icon={<FolderOpen className="w-4 h-4" />}
            label="Load"
            onClick={handleLoad}
            shortcut="Ctrl+O"
          />
        </div>

        {/* Right side - Clear All Button */}
        {(mission?.waypoints.length ?? 0) > 0 && (
          <ToolbarButton
            icon={<Trash2 className="w-4 h-4" />}
            label="Clear All"
            onClick={handleClearAll}
            variant="danger"
          />
        )}
      </div>

      {/* Mission Settings Panel */}
      <MissionSettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      {/* Validation Panel */}
      <ValidationPanel
        isOpen={isValidationOpen}
        onClose={() => setIsValidationOpen(false)}
      />
    </div>
  );
}

/**
 * Toolbar button component
 */
interface ToolbarButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  variant?: 'default' | 'primary' | 'danger';
  shortcut?: string;
}

function ToolbarButton({
  icon,
  label,
  onClick,
  disabled = false,
  active = false,
  variant = 'default',
  shortcut,
}: ToolbarButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={shortcut ? `${label} (${shortcut})` : label}
      className={cn(
        'flex items-center gap-2 mt-1 px-3 py-2 rounded-md text-sm font-medium transition-all',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        variant === 'primary' && 'bg-blue-600 text-white hover:bg-blue-700',
        variant === 'danger' && 'bg-red-600/20 text-red-400 hover:bg-red-600/30 border border-red-700/50',
        variant === 'default' && 'bg-slate-800 text-slate-300 hover:bg-slate-700',
        active && 'ring-2 ring-blue-500'
      )}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}